import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";
import {
  REGIOES_BAIRROS,
  CIDADES,
  CIDADE_COM_REGIAO,
  getRegiaoPorBairro as defaultGetRegiaoPorBairro,
} from "../constants";
import { useTenant } from "../context/TenantContext";
import { isLegacyTenantId } from "../utils/tenantLegacy";

const EMPTY_GEOGRAPHY = {
  regioes_bairros: {},
  cidades: [],
  cidade_com_regiao: "",
};

const hasGeographyData = (geography) => Boolean(
  geography
  && (
    (Array.isArray(geography.cidades) && geography.cidades.length > 0)
    || (geography.regioes_bairros && Object.keys(geography.regioes_bairros).length > 0)
  )
);

const buildGeographyHelpers = ({
  regioes_bairros,
  cidades,
  cidade_com_regiao,
}) => {
  const cidadeTemRegiao = (cidade) => (
    String(cidade || "").trim() === String(cidade_com_regiao || "").trim()
  );

  const getRegiaoPorBairro = (bairro, cidade) => {
    if (!cidadeTemRegiao(cidade)) return "";
    const normalized = String(bairro || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
    if (!normalized) return "";

    for (const [regiao, bairros] of Object.entries(regioes_bairros)) {
      if ((bairros || []).some((item) => String(item || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim() === normalized)) {
        return regiao;
      }
    }
    return defaultGetRegiaoPorBairro(bairro, cidade);
  };

  return {
    regioes_bairros,
    cidades,
    cidade_com_regiao,
    regiao: Object.keys(regioes_bairros),
    cidadeTemRegiao,
    getRegiaoPorBairro,
  };
};

export function useTenantGeography(overrideClienteId = null) {
  const { geography, config } = useTenant();
  const sessionClienteId = config?.cliente_id ?? null;
  const [fetchedGeography, setFetchedGeography] = useState(null);

  const shouldFetchOverride = overrideClienteId != null
    && String(overrideClienteId).trim() !== ""
    && String(overrideClienteId) !== String(sessionClienteId);

  useEffect(() => {
    if (!shouldFetchOverride) {
      setFetchedGeography(null);
      return undefined;
    }

    let cancelled = false;
    apiFetch(`/tenant/geography?cliente_id=${encodeURIComponent(overrideClienteId)}`)
      .then((data) => {
        if (!cancelled) setFetchedGeography({ clienteId:String(overrideClienteId),data });
      })
      .catch(() => {
        if (!cancelled) setFetchedGeography({ clienteId:String(overrideClienteId),data:EMPTY_GEOGRAPHY });
      });

    return () => { cancelled = true; };
  }, [shouldFetchOverride, overrideClienteId]);

  return useMemo(() => {
    const overrideReady = fetchedGeography?.clienteId === String(overrideClienteId);
    const activeGeography = shouldFetchOverride ? (overrideReady ? fetchedGeography.data : null) : geography;
    const effectiveClienteId = shouldFetchOverride ? Number(overrideClienteId) : sessionClienteId;
    const hasTenantGeography = hasGeographyData(activeGeography);

    const useLegacyFallback = !hasTenantGeography && (
      isLegacyTenantId(effectiveClienteId)
      || (effectiveClienteId == null && sessionClienteId == null)
    );

    const source = hasTenantGeography
      ? activeGeography
      : (useLegacyFallback ? null : EMPTY_GEOGRAPHY);

    return { ready: !shouldFetchOverride || overrideReady, ...buildGeographyHelpers({
      regioes_bairros: source?.regioes_bairros || (useLegacyFallback ? REGIOES_BAIRROS : {}),
      cidades: source?.cidades || (useLegacyFallback ? CIDADES : []),
      cidade_com_regiao: source?.cidade_com_regiao || (useLegacyFallback ? CIDADE_COM_REGIAO : ""),
    }) };
  }, [
    geography,
    fetchedGeography,
    shouldFetchOverride,
    overrideClienteId,
    sessionClienteId,
  ]);
}
